import React, { useRef, useState } from 'react';
import { Seo, breadcrumbSchema, webPageSchema, itemListSchema } from '../components/Seo';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';
import { PILLARS } from '../data/content';

const PillarRow = ({ pillar, isOpen, onToggle, rowRef }) => {
  return (
    <div
      data-testid={`pillar-${pillar.id}`}
      id={`pillar-${pillar.id}`}
      ref={rowRef}
      className={`border-l-2 transition-all duration-500 ${isOpen ? 'border-[#9F6A2C] bg-white/40' : 'border-[#B8884A]/35 hover:bg-white/25'}`}
    >
      <button
        onClick={onToggle}
        data-testid={`pillar-toggle-${pillar.id}`}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-6 py-6 md:py-7 px-5 md:px-8 text-left group"
      >
        <div className="flex items-baseline gap-5 md:gap-8 min-w-0">
          <span className="numeral text-2xl md:text-[1.75rem] flex-shrink-0" style={{ color: '#9F6A2C' }}>{pillar.number}</span>
          <span className={`font-serif text-xl md:text-2xl tracking-[-0.015em] transition-colors ${isOpen ? 'text-[#9F6A2C]' : 'text-[#2D1810] group-hover:text-[#9F6A2C]'}`}>
            {pillar.name}
          </span>
        </div>
        <span aria-hidden className={`flex-shrink-0 w-6 h-6 relative transition-transform duration-500 ${isOpen ? 'rotate-45' : ''}`}>
          <span className="absolute left-1/2 top-0 bottom-0 w-px bg-[#9F6A2C] -translate-x-1/2" />
          <span className="absolute top-1/2 left-0 right-0 h-px bg-[#9F6A2C] -translate-y-1/2" />
        </span>
      </button>

      <div className={`grid transition-all duration-500 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <div className="pl-5 md:pl-20 pr-5 md:pr-8 pb-9 md:pb-11">
            <p className="font-serif italic text-[#3A2418]/80 text-base md:text-lg max-w-3xl mb-7">{pillar.tagline}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pillar.categories.map((c, idx) => (
                <Link
                  key={c.name}
                  to={`/enter?category=${encodeURIComponent(c.name)}`}
                  data-testid={`category-card-${pillar.id}-${idx}`}
                  className="group/cat block bg-white border border-[#B8884A]/25 hover:border-[#9F6A2C] p-5 md:p-6 transition-all duration-300 hover:shadow-[0_18px_36px_-22px_rgba(58,36,24,0.35)] hover:-translate-y-0.5"
                >
                  <div className="flex items-start gap-4">
                    <span className="font-sans text-[10px] tracking-[0.3em] uppercase mt-1 flex-shrink-0" style={{ color: '#9F6A2C' }}>
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-[#2D1810] text-lg md:text-[1.2rem] leading-tight group-hover/cat:text-[#9F6A2C] transition-colors">{c.name}</h4>
                      <p className="text-[#3A2418]/65 text-[13px] mt-2 leading-relaxed">{c.desc}</p>
                      <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#9F6A2C] opacity-0 group-hover/cat:opacity-100 transition-opacity">
                        Nominate in this category <ArrowUpRight size={12} />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[#B8884A]/20" />
    </div>
  );
};

const Categories = () => {
  const [openId, setOpenId] = useState(PILLARS[0].id);
  const rowRefs = useRef({});

  const handleToggle = (id) => {
    const closing = openId === id;
    setOpenId(closing ? null : id);
    if (!closing) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = rowRefs.current[id];
          if (!el) return;
          const navOffset = 96;
          const top = el.getBoundingClientRect().top + window.scrollY - navOffset;
          window.scrollTo({ top, behavior: 'smooth' });
        });
      });
    }
  };

  const jumpTo = (id) => {
    setOpenId(id);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = rowRefs.current[id];
        if (!el) return;
        const navOffset = 96;
        const top = el.getBoundingClientRect().top + window.scrollY - navOffset;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  };

  return (
    <div data-testid="page-categories">
      <Seo
        title="AdAura Awards Categories 2026 · 50+ Marketing Award Categories in India"
        description="Explore 50+ AdAura Awards categories across advertising, digital marketing, social media, influencer, brand, agency, PR, startup, creator and AI marketing. India's most comprehensive marketing awards."
        path="/categories"
        jsonLd={[
          webPageSchema({ path: '/categories', name: 'Categories', description: "Explore 50+ AdAura Awards categories across advertising, digital marketing, social media, influencer, brand, agency, PR," }),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Categories', path: '/categories' }]),
          // ItemList schema — enumerates every award category so Google
          // can surface them as sitelinks / list rich results.
          itemListSchema(
            PILLARS.flatMap((p) => (p.categories || []).map((c) => ({
              name: c.name,
              url: `/categories#pillar-${p.id}`,
            }))),
            'AdAura Awards 2026 · Award Categories'
          ),
        ]}
      />
      <PageHeader
        eyebrow="50+ Categories · 10 Pillars"
        title="Award Categories"
        italicWords={['Categories']}
        subtitle="Fifty-plus curated categories, organised across ten pillars of marketing excellence. Designed to make the cases sharper - and the wins harder to argue with."
      />

      {/* PILLAR QUICK-JUMP CARDS */}
      <section className="pb-12 md:pb-14">
        <div className="content-container">
          <Reveal>
            <div className="flex items-center gap-5 mb-6">
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#9F6A2C] font-semibold">Browse Pillars</div>
              <span className="flex-1 h-px bg-[#B8884A]/30" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#3A2418]/55">Tap to expand</span>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="pillar-quick-jump">
              {PILLARS.map((p) => {
                const active = openId === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => jumpTo(p.id)}
                    data-testid={`pillar-jump-${p.id}`}
                    className={`group text-left border p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-20px_rgba(58,36,24,0.4)] ${active ? 'bg-[#2D1810] text-white border-[#2D1810]' : 'bg-white border-[#B8884A]/30 hover:border-[#9F6A2C]'}`}
                  >
                    <div className={`numeral text-base ${active ? 'text-[#D9B26A]' : 'text-[#9F6A2C]'}`}>{p.number}</div>
                    <div className={`font-serif text-[13.5px] md:text-sm leading-tight mt-2 ${active ? 'text-white' : 'text-[#2D1810] group-hover:text-[#9F6A2C]'} transition-colors`}>
                      {p.name}
                    </div>
                    <div className={`text-[10px] tracking-[0.18em] uppercase mt-3 ${active ? 'text-[#D9B26A]' : 'text-[#3A2418]/55'}`}>
                      {p.categories.length} categories
                    </div>
                  </button>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* PILLAR ACCORDION + CATEGORY CARDS */}
      <section className="pb-20 md:pb-24">
        <div className="content-container">
          <div className="border-t border-[#B8884A]/20" />
          <Reveal>
            <div>
              {PILLARS.map((p) => (
                <PillarRow
                  key={p.id}
                  pillar={p}
                  isOpen={openId === p.id}
                  onToggle={() => handleToggle(p.id)}
                  rowRef={(el) => (rowRefs.current[p.id] = el)}
                />
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Categories;
